import React from 'react';

import { FTUX_QA_REVEAL_KEY, getFeatureReveal } from '@/nova/config/featureFlags';
import { useFeatureGate } from '@/nova/hooks/useFeatureGate';

import { QuietBoundary } from '../../QuietBoundary';

/**
 * The strip that reads the store, its own chunk, loaded only once a tester turns the strip on. Lumio's chat imports
 * this shell alone, so the chat's graph never reaches the store (its CT specs mount it with no Provider) and no
 * owner's bundle carries the strip.
 */
const QaStripBody = React.lazy(() => import('./QaStripBody'));

export type QaStripProps = {
  /** A support agent is signed in as the owner (MainLayout's impersonation, through LumioControl): no strip. */
  impersonating?: boolean;
  /**
   * A jump or a Restart started a fresh run there. A phone's Lumio closes on it, so the tester sees the step at once
   * (the user, 2026-10-20); the desktop dock stays, beside the page.
   */
  onStepChange?: () => void;
  /** The chat the strip sits in is open (with onStepChange): the strip that went for a jump comes back with it. */
  open?: boolean;
};

/**
 * The testers' strip at the foot of Lumio's chat, over its input (blueprint section 10; the user, 2026-10-21): shown while 777ftuxqa's reveal key is on and nobody is
 * signed in as the owner, never on the `ftux` flag alone (an environment that pins the flag on shows owners nothing).
 * Typing 777ftuxqa again turns it off; the flow stays on, for every owner.
 */
export const QaStrip = ({ impersonating = false, onStepChange, open }: QaStripProps): React.ReactElement | null => {
  // Re-renders the moment 777ftuxqa flips (every reveal key is in the gate's snapshot).
  useFeatureGate();
  if (impersonating || !getFeatureReveal(FTUX_QA_REVEAL_KEY)) return null;
  // A chunk that cannot load leaves Lumio's chat without the strip, never the studio without its page.
  return (
    <QuietBoundary>
      <React.Suspense fallback={null}>
        <QaStripBody onStepChange={onStepChange} open={open} />
      </React.Suspense>
    </QuietBoundary>
  );
};

export default QaStrip;
